import { createTheme } from "@mui/material/styles";

const primaryColorLight = "rgba(0, 181, 177, 0.2)";
const primaryColor = "#0078adff";
const primaryColorDark = "#0c5273ff";
const secondaryColorLight = "#f0f0f0";
const secondaryColorDark = "#0c5273ff";
const textLight = "rgba(0, 0, 0, 0.65)";
const textDark = "#141414";
const inputBorderColor = "#ddd";
const borderMedium = "#ebebeb";
const borderDark = "#E0E0E0";

const theme = createTheme({
  palette: {
    primary: {
      light: primaryColorLight,
      main: primaryColor,
      dark: primaryColorDark,
      contrastText: "#fff",
    },
    secondary: {
      light: secondaryColorLight,
      main: textLight,
      dark: secondaryColorDark,
    },
    success: {
      light: "#E7F8EE",
      main: "#2E8E60",
    },
    error: {
      light: "#F8E7E7",
      main: "#CD3232",
    },
    warning: {
      light: "#FDF5DA",
      main: "#F7B04A",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 992,
      lg: 1200,
      xl: 1536,
    },
  },
  text: {
    color: textDark,
  },
  typography: {
    fontFamily: "var(--primary-font), sans-serif",
    fontSize: 12,
    color: textDark,
    h1: {
      fontFamily: "var(--font-bold), sans-serif",
      fontSize: 40,
      letterSpacing: "-0.3px",
      "@media (max-width:991px)": {
        fontSize: 38,
      },
    },
    h2: {
      fontFamily: "var(--font-bold), sans-serif",
      fontSize: 32,
    },
    h3: {
      fontFamily: "var(--font-semibold), sans-serif",
      fontSize: 24,
      lineHeight: "28px",
      letterSpacing: "-0.3px",
    },
    h4: {
      fontFamily: "var(--font-medium), sans-serif",
      fontSize: 22,
    },
    h5: {
      fontFamily: "var(--font-medium), sans-serif",
      fontSize: 20,
      lineHeight: "32px",
    },
    h6: {
      fontFamily: "var(--font-semibold), sans-serif",
      fontSize: 18,
      lineHeight: "24px",
    },
    subtitle1: {
      fontFamily: "var(--font-medium), sans-serif",
      fontSize: 18,
      color: textLight,
      lineHeight: "24px",
    },
    subtitle2: {
      fontFamily: "var(--font-medium), sans-serif",
      fontSize: 16,
      lineHeight: "24px",
    },
    body1: {
      fontFamily: "var(--primary-font), sans-serif",
      fontSize: 18,
      lineHeight: "24px",
    },
    body2: {
      fontFamily: "var(--primary-font), sans-serif",
      fontSize: 14,
    },
    caption: {
      fontFamily: "var(--font-medium), sans-serif",
      fontSize: 12,
      lineHeight: "16px",
      color: textLight,
      display: "inline-block",
    },
    button: {
      fontFamily: "var(--font-bold), sans-serif",
      fontSize: 18,
    },
  },
  components: {
    // Buttons
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          lineHeight: "24px",
          padding: "12px 24px",
          borderRadius: "250px",
          textTransform: "none",
          minWidth: "unset",
          fontFamily: "var(--font-semibold)",
          fontSize: 18,
        },
        contained: {
          fontSize: 18,
        },
        outlined: {
          border: "1px solid var(--border-dark)",
          color: primaryColorDark,
        },
        sizeLarge: {
          padding: "16px 32px",
          fontSize: 24,
        },
        text: {
          padding: "8px 6px",
          borderRadius: "4px",
          minWidth: "auto",
          fontFamily: "var(--font-semibold), sans-serif",
          fontSize: 16,
          color: secondaryColorDark,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            opacity: 0.5,
          },
          "&.Mui-disabled > img": {
            opacity: 0.5,
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0,0,0,.2) !important",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            // backgroundColor: secondaryColorLight,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        // root: {
        //   fontSize: "16px",
        //   height: "28px",
        //   color: "rgba(56, 56, 56, 0.86)",
        //   backgroundColor: "rgb(243, 246, 248)",
        // },
        deleteIcon: {
          color: "#535353",
          fontSize: "16px",
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: "14px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          width: "100%",
        },
      },
      variants: [
        {
          props: { variant: "dashed" },
          style: {
            textTransform: "none",
            border: `2px dashed secondary.light`,
          },
        },
        {
          props: { variant: "dashed", color: "secondary.light" },
          style: {
            border: `4px dashed secondary.light`,
          },
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0,0,0,.2)",
          },
        },
        input: {
          padding: "14px",
          height: "auto",
          color: textDark,
          fontFamily: ["var(--primary-font)", "sans-serif"].join(","),
          fontSize: "16px",
        },
        notchedOutline: {
          borderColor: inputBorderColor,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          width: "100%",
          "& .MuiInputBase-multiline": {
            padding: 0,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          "&::before": {
            borderBottom: "2px solid rgba(0, 0, 0, 0.65)",
          },
        },
        input: {
          fontFamily: "var(--font-semibold)",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "16px",
          color: textLight,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: textLight,
          fontFamily: "var(--font-medium)",
          fontSize: "16px",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: "16px",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: "#B8B8B8",
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          fontSize: "24px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderColor: borderMedium,
          padding: "14px",
          borderRadius: "12px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          backgroundColor: "#fff",
          "&.MuiPopover-paper": {
            borderRadius: "12px",
          },
        },
      },
    },
    MuiTimelineItem: {
      styleOverrides: {
        root: {
          minHeight: "90px",
        },
      },
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          padding: "8px",
          margin: 0,
          zIndex: 1,
        },
        filled: {
          boxShadow: "0px 0px 1px 6px rgb(191, 232, 231)",
        },
      },
    },
    MuiTimelineContent: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingLeft: "30px",
        },
      },
    },
    MuiTimelineOppositeContent: {
      styleOverrides: {
        root: {
          padding: 0,
          flex: 0,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: borderDark,
        },
        dashed: {
          borderStyle: "dashed",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          "&.MuiTabs-vertical .MuiTab-root": {
            minHeight: "51px",
            fontFamily: "var(--font-medium)",
            textTransform: "capitalize",
            flexDirection: "row",
          },
          // "& .Mui-selected svg path": {
          //   fill: "currentColor",
          //   stroke: "currentColor",
          // },
          "&.MuiTabs-vertical .MuiTabs-indicator": {
            width: "8px",
            height: "51px",
            borderRadius: "4px 0 0 4px",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) =>
          theme.unstable_sx({
            maxWidth: {
              // sm: "600px",
              // md: "900px",
            },
          }),
      },
    },
  },
});

export const tableStyle = {
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "var(--color-primary-50)", // Set the header background color
    color: "white", // Set the header text color
    fontWeight: "700 !important",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontFamily: "var(--font-families-body-sbold)",
    fontSize: "var(--font-sizes-body-sbold)",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "none", // Remove the border from individual cells
  },
  "& .MuiDataGrid-cellContent": {
    fontFamily: "var(--font-families-body-m)",
    fontSize: "var(--font-sizes-body-sbold)",
  },
  "& .MuiDataGrid-columnSeparator": {
    display: "none", // Remove the separator lines between column headers
  },
  "& .MuiDataGrid-sortIcon": {
    color: "white",
  },
};

export default theme;
